/**
 * Значок категории: набор иконок и цвет.
 *
 * Значок здесь не украшение. В списке из тридцати строк человек находит
 * нужную по форме и цвету, а не вчитываясь в названия, — поэтому у каждой
 * категории есть и то, и другое, и оба хранятся в самой категории.
 *
 * Значок — иконка Font Awesome Pro, тот же набор, что в макетах (сборка
 * `tools/build-icon-pack.mjs`). Старые имена lucide и эмодзи первых версий
 * переводятся на новые при чтении данных, а чужой символ, которого в таблице
 * нет, рисуется как есть.
 *
 * Цвета не берутся из головы: если человек его не выбирал, цвет говорит о
 * части плана — зелёное для повседневных нужд, розовое для свободных трат,
 * серое для тех, что решаются при записи. Так список остаётся честным даже
 * там, где никто ничего не настраивал.
 */
import type { Category } from './categories.js';
import { Icon, hasIcon } from './icon.js';

export const CATEGORY_COLORS = ['pink', 'violet', 'blue', 'green', 'amber', 'red', 'gray'] as const;
export type CategoryColor = typeof CATEGORY_COLORS[number];

export const COLOR_LABEL: Record<CategoryColor, string> = {
  pink: 'Розовый', violet: 'Фиолетовый', blue: 'Синий', green: 'Зелёный',
  amber: 'Жёлтый', red: 'Красный', gray: 'Серый',
};

const KNOWN = new Set<string>(CATEGORY_COLORS);

/** Цвет категории: выбранный человеком либо по части плана. */
export function colorOf(c?: Pick<Category, 'color' | 'defaultPart' | 'kind'> | null): CategoryColor {
  if (c?.color && KNOWN.has(c.color)) return c.color as CategoryColor;
  if (!c) return 'gray';
  if (c.kind === 'income') return 'blue';
  return c.defaultPart === 'need' ? 'green' : c.defaultPart === 'want' ? 'pink' : 'gray';
}

export function CategoryGlyph({ icon, color, size = 'm' }: {
  icon: string;
  color: CategoryColor;
  /**
   * `xs` — внутри плашки, без подложки; `f` — в строке поля формы, рядом с
   * названием; `s` — в строке списка, `m` — в поле, `l` — в форме категории.
   */
  size?: 'xs' | 'f' | 's' | 'm' | 'l';
}) {
  const px = size === 'l' ? 38 : size === 's' ? 15 : size === 'xs' ? 14 : size === 'f' ? 13 : 19;
  return (
    <span className={`catico c-${color} s-${size}`} aria-hidden="true">
      {hasIcon(icon || 'circle-ellipsis') ? <Icon name={icon || 'circle-ellipsis'} size={px} /> : <i>{icon}</i>}
    </span>
  );
}

/** Значок категории прямо из неё: чаще всего нужен именно такой вызов. */
export const GlyphOf = ({ category, size }: { category?: Category; size?: 's' | 'm' | 'l' }) => (
  <CategoryGlyph icon={category?.icon || 'circle-ellipsis'} color={colorOf(category)} {...(size ? { size } : {})} />
);
